import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/react'

import { ApplyTemplateStatus, Maybe, OwnerPlanFragment } from '../../generated/graphql'
import { updateOwnerPlansCache } from '../cacheUtils'
import { pluralize } from '../utils'
import useNotification from './useNotification'

/**
 * The fields shared by the applyTemplateResponse query and the applyTemplateComplete
 * subscription payloads. The two are generated separately, so callers pass this shape
 * instead of either generated type.
 */
export type ApplyTemplateResult = {
  correlationId: string
  status: ApplyTemplateStatus
  studentCount: number
  templateTitle: string
  plansCreated?: Maybe<Array<OwnerPlanFragment>>
  /** Set by the server when the status is FAILED; absent on older payloads */
  failureReason?: Maybe<string>
}

const MAX_TITLE_LENGTH = 25

// The subscription in App.tsx and the one-shot query on the template page both receive the
// same correlationId from router state, so a fast job can reach both. Remember what was
// reported so the advisor sees one toast. Intentionally unbounded: a tab handles a few
// applies per session, and a reload clears it.
const handledCorrelationIds = new Set<string>()

/** Forgets the correlationIds reported so far. Meant for tests. */
export const resetHandledApplyTemplateResults = () => handledCorrelationIds.clear()

const displayTitle = (templateTitle: string) => {
  const title = templateTitle || 'Template'
  return title.length > MAX_TITLE_LENGTH ? `${title.substring(0, MAX_TITLE_LENGTH)}...` : title
}

/**
 * Handles a terminal apply-template response. COMPLETE updates the owner plan caches and shows
 * the success toast; FAILED shows the error toast with the server's reason. Returns true when
 * the status is terminal so the caller can drop the correlationId, false while RUNNING.
 */
const useApplyTemplateResult = () => {
  const { cache } = useApolloClient()
  const { showSuccessNotif, showErrorNotif } = useNotification()

  return useCallback(
    (result: ApplyTemplateResult): boolean => {
      if (result.status === ApplyTemplateStatus.Running) {
        return false
      }
      if (
        result.status !== ApplyTemplateStatus.Complete &&
        result.status !== ApplyTemplateStatus.Failed
      ) {
        // A status this build does not know (older API, future value) must not be reported as a
        // failure; keep listening and let Sentry show the mismatch
        Sentry.captureMessage(`Unexpected apply template status: ${String(result.status)}`)
        return false
      }
      // The cache write runs for every payload that carries plans, because the one-shot query and
      // the subscription can each deliver them; updateOwnerPlansCache keeps one copy per plan
      if (result.status === ApplyTemplateStatus.Complete) {
        result.plansCreated?.forEach((plan) =>
          updateOwnerPlansCache(cache, plan.ownerAppUserId, plan)
        )
      }
      if (handledCorrelationIds.has(result.correlationId)) {
        return true
      }
      handledCorrelationIds.add(result.correlationId)

      const title = displayTitle(result.templateTitle)
      const students = pluralize(result.studentCount, 'student')
      if (result.status === ApplyTemplateStatus.Complete) {
        showSuccessNotif(`${title} applied to ${students}`)
      } else {
        const reason = result.failureReason ? ` ${result.failureReason}` : ''
        showErrorNotif(`${title} could not be applied to ${students}.${reason}`)
      }
      return true
    },
    [cache, showErrorNotif, showSuccessNotif]
  )
}

export default useApplyTemplateResult
